/**
 * Audio Processing Configuration
 * 
 * This file contains optimal WebRTC audio processing settings for noise suppression,
 * echo cancellation, and voice quality enhancement.
 */

export interface AudioProcessingConfig {
  // Basic audio processing
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;

  // Advanced noise suppression
  noiseSuppressionLevel?: 'low' | 'medium' | 'high' | 'very-high';
  echoCancellationType?: 'system' | 'browser';

  // Audio quality settings
  sampleRate: number;
  channelCount: number;

  // Additional processing for better noise suppression
  googEchoCancellation?: boolean;
  googAutoGainControl?: boolean;
  googNoiseSuppression?: boolean;
  googHighpassFilter?: boolean;
  googTypingNoiseDetection?: boolean;
  googAudioMirroring?: boolean;

  // Device selection
  deviceId?: string | { exact: string } | { ideal: string };
}

export interface AudioConstraintOptions {
  exactDeviceId?: boolean;
  preferCompatibility?: boolean;
  userAgent?: string;
}

const SAFARI_EXCLUDED_BROWSERS_RE = /Chrome|Chromium|CriOS|Edg|OPR|OPiOS|Firefox|FxiOS|SamsungBrowser|Android/i;

export function isSafariLikeBrowser(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (!ua) return false;
  return /Safari/i.test(ua) && !SAFARI_EXCLUDED_BROWSERS_RE.test(ua);
}

function shouldUseCompatibilityMode(options?: AudioConstraintOptions): boolean {
  return options?.preferCompatibility === true || isSafariLikeBrowser(options?.userAgent);
}

/**
 * Enhanced noise suppression configuration for voice communication
 * Optimized for clapping, blowing air, and sudden sounds
 */
export const enhancedNoiseSuppressionConfig: AudioProcessingConfig = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  noiseSuppressionLevel: 'high',
  echoCancellationType: 'system',
  sampleRate: 48000,
  channelCount: 1,
  // Advanced Google-specific processing
  googEchoCancellation: true,
  googAutoGainControl: true,
  googNoiseSuppression: true,
  googHighpassFilter: true,
  googTypingNoiseDetection: true,
  googAudioMirroring: false,
};

/**
 * Standard audio processing configuration for voice communication
 * Optimized for noise suppression, echo cancellation, and voice clarity
 */
export const standardAudioConfig: AudioProcessingConfig = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  sampleRate: 48000,
  channelCount: 1,
};

/**
 * High-quality audio processing configuration
 * Enhanced settings for professional voice communication
 */
export const highQualityAudioConfig: AudioProcessingConfig = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  noiseSuppressionLevel: 'high',
  echoCancellationType: 'system',
  sampleRate: 48000,
  channelCount: 1,
  googEchoCancellation: true,
  googAutoGainControl: true,
  googNoiseSuppression: true,
  googHighpassFilter: true,
  googTypingNoiseDetection: true,
  googAudioMirroring: false,
};

/**
 * Low-latency audio processing configuration
 * Optimized for real-time communication with minimal delay
 */
export const lowLatencyAudioConfig: AudioProcessingConfig = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  sampleRate: 48000,
  channelCount: 1,
};

/**
 * Get audio constraints for getUserMedia with enhanced noise suppression
 * @param deviceId - Optional device ID to use
 * @param config - Audio processing configuration
 * @returns MediaStreamConstraints for audio
 */
export function getAudioConstraints(
  deviceId?: string,
  config: AudioProcessingConfig = enhancedNoiseSuppressionConfig,
  options?: AudioConstraintOptions
): MediaStreamConstraints['audio'] {
  const compatibilityMode = shouldUseCompatibilityMode(options);
  const constraints: any = {
    deviceId: deviceId
      ? (options?.exactDeviceId === false || compatibilityMode ? { ideal: deviceId } : { exact: deviceId })
      : undefined,
    echoCancellation: config.echoCancellation,
    noiseSuppression: config.noiseSuppression,
    autoGainControl: config.autoGainControl,
  };

  if (compatibilityMode) {
    return constraints;
  }

  constraints.sampleRate = config.sampleRate;
  constraints.channelCount = config.channelCount;

  // Add advanced noise suppression options if available
  if (config.noiseSuppressionLevel) {
    constraints.noiseSuppressionLevel = config.noiseSuppressionLevel;
  }

  if (config.echoCancellationType) {
    constraints.echoCancellationType = config.echoCancellationType;
  }

  // Add Google-specific processing options for Chrome/Chromium
  if (config.googEchoCancellation !== undefined) {
    constraints.googEchoCancellation = config.googEchoCancellation;
  }

  if (config.googAutoGainControl !== undefined) {
    constraints.googAutoGainControl = config.googAutoGainControl;
  }

  if (config.googNoiseSuppression !== undefined) {
    constraints.googNoiseSuppression = config.googNoiseSuppression;
  }

  if (config.googHighpassFilter !== undefined) {
    constraints.googHighpassFilter = config.googHighpassFilter;
  }

  if (config.googTypingNoiseDetection !== undefined) {
    constraints.googTypingNoiseDetection = config.googTypingNoiseDetection;
  }

  if (config.googAudioMirroring !== undefined) {
    constraints.googAudioMirroring = config.googAudioMirroring;
  }

  return constraints;
}

/**
 * Get LiveKit audio capture defaults with enhanced noise suppression
 * @param deviceId - Optional device ID to use
 * @param config - Audio processing configuration
 * @returns AudioCaptureOptions for LiveKit
 */
export function getLiveKitAudioDefaults(
  deviceId?: string,
  config: AudioProcessingConfig = enhancedNoiseSuppressionConfig,
  options?: AudioConstraintOptions
) {
  const compatibilityMode = shouldUseCompatibilityMode(options);
  const defaults: any = {
    deviceId,
    echoCancellation: config.echoCancellation,
    noiseSuppression: config.noiseSuppression,
    autoGainControl: config.autoGainControl,
  };

  if (compatibilityMode) {
    return defaults;
  }

  defaults.sampleRate = config.sampleRate;
  defaults.channelCount = config.channelCount;

  // Add advanced noise suppression options if available
  if (config.noiseSuppressionLevel) {
    defaults.noiseSuppressionLevel = config.noiseSuppressionLevel;
  }

  if (config.echoCancellationType) {
    defaults.echoCancellationType = config.echoCancellationType;
  }

  // Add Google-specific processing options for Chrome/Chromium
  if (config.googEchoCancellation !== undefined) {
    defaults.googEchoCancellation = config.googEchoCancellation;
  }

  if (config.googAutoGainControl !== undefined) {
    defaults.googAutoGainControl = config.googAutoGainControl;
  }

  if (config.googNoiseSuppression !== undefined) {
    defaults.googNoiseSuppression = config.googNoiseSuppression;
  }

  if (config.googHighpassFilter !== undefined) {
    defaults.googHighpassFilter = config.googHighpassFilter;
  }

  if (config.googTypingNoiseDetection !== undefined) {
    defaults.googTypingNoiseDetection = config.googTypingNoiseDetection;
  }

  if (config.googAudioMirroring !== undefined) {
    defaults.googAudioMirroring = config.googAudioMirroring;
  }

  return defaults;
}

/**
 * Audio processing presets for different use cases
 */
export const audioPresets = {
  standard: standardAudioConfig,
  highQuality: highQualityAudioConfig,
  lowLatency: lowLatencyAudioConfig,
  enhancedNoiseSuppression: enhancedNoiseSuppressionConfig,
} as const;

export type AudioPreset = keyof typeof audioPresets; 
